import type { ReactNode } from 'react';
import { Card, Chip, MicroLabel, SectionHead } from './ui/primitives';
import type { BrandVoice } from './types';

/**
 * Who the brand is: the long-form description beside the facts that place it.
 *
 * The facts rail carries the things people ask about a brand kit that aren't
 * in the kit — who owns it, what it belongs to, where it lives — and which
 * previously lived nowhere on the page.
 */
export function BrandStory({
	description,
	facts,
	traits,
	action,
}: {
	description?: string | null;
	facts: { label: string; value: ReactNode }[];
	/** `brandPersonality.traits` — the adjectives, shown under the story. */
	traits?: string[] | null;
	action?: ReactNode;
}) {
	return (
		<>
			<SectionHead title="Who we are" action={action} />
			<Card padded>
				<div className="grid gap-7 lg:grid-cols-[minmax(0,1fr)_14rem]">
					<div>
						{description ? (
							<p className="max-w-[40rem] whitespace-pre-line text-[0.95rem] leading-[1.7] text-text-1">
								{description}
							</p>
						) : (
							<p className="text-sm text-text-4">
								No brand story yet. This is what agencies and suppliers read first.
							</p>
						)}
						{traits && traits.length > 0 && (
							<div className="mt-5 flex flex-wrap gap-1.5">
								{traits.map((trait) => (
									<Chip key={trait}>{trait}</Chip>
								))}
							</div>
						)}
					</div>

					{facts.length > 0 && (
						<div className="flex flex-col gap-3.5 lg:border-l lg:border-line-1 lg:pl-6">
							{facts.map((fact) => (
								<div key={fact.label}>
									<MicroLabel className="mb-1 block">{fact.label}</MicroLabel>
									<div className="text-sm text-text-1">{fact.value}</div>
								</div>
							))}
						</div>
					)}
				</div>
			</Card>
		</>
	);
}

/**
 * Tone of voice.
 *
 * The mockup paired a "we are" list with a "we're not" list, which reads well
 * and has no field behind it — `brandPersonality` carries traits, values and a
 * tone, and nothing negative. Rather than invent a field, this shows what is
 * actually stored: the tone as prose, traits and values as two labelled sets.
 *
 * If "we're not" turns out to matter, it is a backend ask, not a UI trick.
 */
export function BrandVoiceSection({ voice, action }: { voice: BrandVoice; action?: ReactNode }) {
	const traits = voice.traits ?? [];
	const values = voice.values ?? [];
	const empty = !voice.tone && traits.length === 0 && values.length === 0;

	return (
		<>
			<SectionHead title="Tone of voice" action={action} />
			<Card padded>
				{empty ? (
					<p className="text-sm text-text-4">
						No tone of voice captured yet — how the brand sounds, and the words it does
						and doesn&apos;t use.
					</p>
				) : (
					<div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
						{voice.tone && (
							<div>
								<MicroLabel className="mb-2.5 block">How we sound</MicroLabel>
								<p className="max-w-[30rem] whitespace-pre-line text-[0.95rem] leading-[1.65] text-text-2">
									{voice.tone}
								</p>
							</div>
						)}
						<div className="flex flex-col gap-5">
							{traits.length > 0 && <ChipSet label="Traits" words={traits} />}
							{values.length > 0 && <ChipSet label="Values" words={values} />}
						</div>
					</div>
				)}
			</Card>
		</>
	);
}

function ChipSet({ label, words }: { label: string; words: string[] }) {
	return (
		<div>
			<MicroLabel className="mb-2 block">{label}</MicroLabel>
			<div className="flex flex-wrap gap-1.5">
				{words.map((word) => (
					<Chip key={word}>{word}</Chip>
				))}
			</div>
		</div>
	);
}
